import { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function Footer() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const quickLinks = [
    { label: 'Home', to: '/' },
    { label: 'Shop All', to: '/shop' },
    { label: 'Contact Us', to: '/contact' },
    { label: 'FAQ', to: '/faq' },
    { label: 'Track Order', to: '/track-order' },
  ];

  const categoryLinks = [
    { name: 'Beauty & Skincare', icon: 'ri-heart-line', to: '/shop?category=beauty' },
    { name: 'Kitchen Essentials', icon: 'ri-restaurant-line', to: '/shop?category=kitchen' },
    { name: 'Health & Wellness', icon: 'ri-pulse-line', to: '/shop?category=health' },
    { name: 'Electronics', icon: 'ri-smartphone-line', to: '/shop?category=electronics' },
  ];

  return (
    <footer
      ref={ref}
      className="relative overflow-hidden text-white"
      style={{ background: 'linear-gradient(135deg, #1a0a00, #2d0f0f, #1a0a1a)' }}
    >
      <div className="h-1 w-full" style={{ background: 'linear-gradient(90deg, #f97316, #ec4899, #f59e0b, #ef4444, #f97316)', backgroundSize: '200% 100%', animation: 'gradientShift 4s ease infinite' }} />

      <div className="absolute top-0 left-0 w-96 h-96 rounded-full opacity-10 pointer-events-none"
        style={{ background: 'radial-gradient(circle, #f97316, transparent)', filter: 'blur(80px)', transform: 'translate(-30%, -30%)' }} />
      <div className="absolute bottom-0 right-0 w-96 h-96 rounded-full opacity-10 pointer-events-none"
        style={{ background: 'radial-gradient(circle, #ec4899, transparent)', filter: 'blur(80px)', transform: 'translate(30%, 30%)' }} />

      <div className="max-w-7xl mx-auto px-6 py-16 relative z-10">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-12 mb-12">
          {/* Brand */}
          <div className={`transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="mb-5">
              <h2 className="text-4xl font-black gradient-text-fire mb-1">AISHOPIO</h2>
              <div className="h-0.5 w-20 rounded-full" style={{ background: 'linear-gradient(90deg, #f97316, #ec4899)' }} />
            </div>
            <p className="text-gray-400 mb-2 leading-relaxed text-sm">
              Dubai&apos;s trusted destination for premium quality products. Shop with confidence and style.
            </p>
            <p className="text-gray-500 text-xs">
              <i className="ri-map-pin-line text-orange-500 mr-1"></i>
              Dubai, United Arab Emirates
            </p>
          </div>

          {/* Quick Links */}
          <div className={`transition-all duration-700 delay-100 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <h3 className="text-lg font-bold mb-6 flex items-center gap-2">
              <span className="w-1 h-5 rounded-full inline-block" style={{ background: 'linear-gradient(180deg, #f97316, #ec4899)' }} />
              Quick Links
            </h3>
            <ul className="space-y-3">
              {quickLinks.map((link, i) => (
                <li key={i}>
                  <Link
                    to={link.to}
                    className="group flex items-center gap-2 text-gray-400 text-sm transition-all duration-300 hover:text-orange-400 cursor-pointer"
                  >
                    <i className="ri-arrow-right-s-line text-orange-500 opacity-0 -ml-4 group-hover:opacity-100 group-hover:ml-0 transition-all duration-300"></i>
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Categories */}
          <div className={`transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <h3 className="text-lg font-bold mb-6 flex items-center gap-2">
              <span className="w-1 h-5 rounded-full inline-block" style={{ background: 'linear-gradient(180deg, #ec4899, #f59e0b)' }} />
              Categories
            </h3>
            <ul className="space-y-3">
              {categoryLinks.map((cat, i) => (
                <li key={i}>
                  <Link
                    to={cat.to}
                    className="group flex items-center gap-2 text-gray-400 text-sm transition-all duration-300 hover:text-pink-400 cursor-pointer"
                  >
                    <i className={`${cat.icon} text-pink-500 opacity-60 group-hover:opacity-100 transition-opacity duration-300`}></i>
                    {cat.name}
                  </Link>
                </li>
              ))}
            </ul>
            <div className="mt-6 p-3 rounded-xl" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}>
              <p className="text-xs text-gray-500 mb-1">Delivery across UAE</p>
              <p className="text-xs text-orange-400 font-medium">All 7 Emirates &bull; Free over AED 200</p>
            </div>
          </div>

          {/* Contact Info */}
          <div className={`transition-all duration-700 delay-300 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <h3 className="text-lg font-bold mb-6 flex items-center gap-2">
              <span className="w-1 h-5 rounded-full inline-block" style={{ background: 'linear-gradient(180deg, #f59e0b, #f97316)' }} />
              Contact Us
            </h3>
            <div className="space-y-4">
              <a href="https://wa.me/971500000000" target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-3 p-3 rounded-xl transition-all duration-300 hover:scale-105 cursor-pointer"
                style={{ background: 'rgba(37,211,102,0.1)', border: '1px solid rgba(37,211,102,0.2)' }}>
                <div className="w-8 h-8 flex items-center justify-center rounded-lg" style={{ background: '#25d366' }}>
                  <i className="ri-whatsapp-line text-white text-sm"></i>
                </div>
                <div>
                  <p className="text-xs text-gray-500">WhatsApp</p>
                  <p className="text-sm text-green-400 font-medium">+971 50 000 0000</p>
                </div>
              </a>
              <div className="flex items-center gap-3 text-sm text-gray-400">
                <i className="ri-mail-line text-orange-500"></i>
                <span>hello@aishopio.ae</span>
              </div>
              <div className="flex items-center gap-3 text-sm text-gray-400">
                <i className="ri-time-line text-orange-500"></i>
                <span>24/7 Customer Support</span>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t pt-8" style={{ borderColor: 'rgba(255,255,255,0.08)' }}>
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-gray-500 text-sm text-center sm:text-left">
              © 2026 <span className="text-orange-400 font-semibold">AiShopio</span>. All rights reserved. Dubai, UAE.
            </p>
            <div className="flex items-center gap-4 sm:gap-6 flex-wrap justify-center">
              <Link to="/privacy" className="text-gray-500 hover:text-orange-400 transition-colors duration-300 text-sm cursor-pointer">
                Privacy Policy
              </Link>
              <Link to="/terms" className="text-gray-500 hover:text-orange-400 transition-colors duration-300 text-sm cursor-pointer">
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
